/**
 * @fileoverview AR-805/AR-670 — Google Classroom integration page. Open to
 * every tutor and manager: the Workspace connection is per user, so each
 * teacher connects their own Google account here. Prototype source:
 * arcmosaico-prototype/app/settings/school/google-classroom/page.tsx
 * Gated on the school-scoped arc-google-classroom flag.
 */

import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { Box, LoadingOverlay } from '@mantine/core'

import { Layout } from 'components/Layout'
import { ArcPageShell } from 'components/ArcPageShell'
import { CurrentUserContextProvider, useCurrentUser } from 'context/useCurrentUser'
import { NotificationProvider } from 'context'
import { useClassroomIntegrationEnabled } from 'integrations/classroom'
import { GoogleClassroomIntegration } from './components/GoogleClassroomIntegration'

function SchoolIntegrationsView() {
  const { t } = useTranslation()
  const { enabled, loading } = useClassroomIntegrationEnabled()
  const { currentUser } = useCurrentUser()
  // Manager profile supervises links; only teachers connect an account —
  // the subtitle must not promise a Connect the page will not offer.
  const isManager = currentUser?.profile_used === 'manager'

  // The settings card that leads here is hidden with the flag, but a bookmark
  // or a typed URL must not reach the page either.
  if (loading) {
    return (
      <Box pos='relative' style={{ minHeight: '100vh' }}>
        <LoadingOverlay visible />
      </Box>
    )
  }

  if (!enabled) return <Navigate to='/error_permission' replace />

  return (
    <Layout>
      <ArcPageShell
        title={t('classroom.gcwSettingsTitle')}
        subtitle={t(isManager ? 'classroom.gcwManagerPageSubtitle' : 'classroom.gcwPageSubtitle')}
        backTo='/teacher/settings'
        backLabel={t('classroom.backToSettings')}
      >
        <GoogleClassroomIntegration />
      </ArcPageShell>
    </Layout>
  )
}

export function SchoolIntegrationsPage() {
  return (
    <CurrentUserContextProvider>
      <NotificationProvider>
        <SchoolIntegrationsView />
      </NotificationProvider>
    </CurrentUserContextProvider>
  )
}
